import { apiUrl } from "../utils/api";
import type { DuplicateCheckCandidate } from "../utils/contactMatchKey";

/**
 * Thin wrapper around the existing RBAC-scoped contact search
 * (`GET /api/contact?search=`) used by the live duplicate-check at contact
 * creation time. Returns a slim candidate shape; never throws on a non-OK
 * response (returns []), and re-throws AbortError so the caller can ignore it.
 *
 * NOTE: the search is RBAC-scoped — a user only sees contacts in their own
 * visibility scope, so "no warning" does NOT prove "no duplicate" (the panel
 * states this explicitly).
 */
export const searchContactsForDuplicateCheck = async (
  term: string,
  signal?: AbortSignal,
): Promise<DuplicateCheckCandidate[]> => {
  const q = term.trim();
  if (q.length < 2) return [];

  const res = await fetch(apiUrl("contact", { search: q, limit: 25 }), { signal });
  if (!res.ok) return [];

  const body = await res.json().catch(() => null);
  const rows: any[] = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.items)
        ? body.items
        : [];

  return rows.map((row): DuplicateCheckCandidate => {
    const defaultEmail =
      row?.defaultEmail?.value ??
      row?.defaultEmail ??
      (Array.isArray(row?.communications)
        ? row.communications.find((c: any) => c?.type === "email")?.value
        : undefined) ??
      null;
    return {
      id: Number(row?.id),
      lastname: row?.lastname ?? undefined,
      firstName: row?.firstName ?? row?.vorname ?? undefined,
      displayName: row?.displayName ?? undefined,
      birthDate: row?.birthDate ?? undefined,
      type: row?.type?.type ?? row?.type ?? null,
      contactNbrInt: row?.contactNbrInt ?? null,
      defaultEmail,
      city: row?.defaultAddress?.city ?? row?.city ?? null,
    };
  });
};
